// Which fuel price the map draws, and the cheapest-ten lists behind the Now
// box's fuel line.
//
// Imports nothing, like cluster.ts and pin-layout.ts, so the rules can be
// tested under plain node — live.ts reads import.meta.env and cannot be.

/** The two fuel grades the Transport box can put on the map. */
export type FuelGrade = 'petrol' | 'diesel';

type Band = 'ok' | 'info' | 'minor' | 'major' | 'unknown';

/** Price, label and band per grade — see the backend's LiveFeature.grades. */
export type FuelGrades = Partial<Record<FuelGrade, { price: number; label: string; severity: Band }>>;

interface Forecourt {
  readonly id: string;
  readonly providerId: string;
  readonly title: string;
  readonly status: string;
  readonly severity: Band;
  readonly grades?: FuelGrades;
}

/** The tank the Cheapest fuel panel prices a fill-up for — the average car's. */
export const TANK_LITRES = 55;

/**
 * Indicative cost of filling a tank from empty at a pence-per-litre price:
 * 150.9p × 55 L → "£83.00". Indicative because nobody arrives empty and
 * tanks vary; it is there to turn a 3p difference into pounds.
 */
export function tankCost(pencePerLitre: number, litres = TANK_LITRES): string {
  if (!Number.isFinite(pencePerLitre) || !Number.isFinite(litres)) return '';
  return `£${(Math.round(pencePerLitre * litres) / 100).toFixed(2)}`;
}

/** Anything other than 'diesel' reads as petrol: storage and markup are strings. */
export const parseFuelGrade = (v: unknown): FuelGrade => (v === 'diesel' ? 'diesel' : 'petrol');

/**
 * What a forecourt draws for the chosen grade: its price and its band within
 * the local spread for that grade.
 *
 * Petrol is `status` and `severity` as they always were, so a backend that
 * predates `grades` still draws. A forecourt that sells no diesel says so
 * rather than keeping its petrol price, which would be a number for the wrong
 * fuel under a switch saying diesel — and it stays grey, so it cannot read as
 * a bargain.
 */
export function fuelView<S extends Band>(
  f: { status: string; severity: S; grades?: FuelGrades },
  grade: FuelGrade,
): { label: string; severity: Band } {
  if (grade === 'petrol') return { label: f.status, severity: f.severity };
  const g = f.grades?.diesel;
  return g ? { label: g.label, severity: g.severity } : { label: 'no diesel', severity: 'unknown' };
}

/** The n cheapest forecourts for a grade, cheapest first, ties by name. */
export function cheapestFuel<F extends Forecourt>(
  features: readonly F[],
  grade: FuelGrade,
  n = 10,
): F[] {
  return features
    .filter((f) => f.providerId === 'fuel' && Number.isFinite(f.grades?.[grade]?.price))
    .sort((a, b) => a.grades![grade]!.price - b.grades![grade]!.price
      || a.title.localeCompare(b.title))
    .slice(0, n);
}
